'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './ColorField.module.css';

export interface ColorFieldProps {
  /**
   * Label text
   */
  label?: string;

  /**
   * Hex colour value, e.g. `#13319a`. Empty string renders the unset state.
   */
  value: string;

  /**
   * Called with a normalised `#rrggbb` value, or `''` when cleared.
   */
  onChange: (value: string) => void;

  /**
   * Helper/description text
   */
  helperText?: string;

  /**
   * Error message (also sets invalid state)
   */
  error?: string;

  disabled?: boolean;

  /**
   * Additional class names for the root element
   */
  className?: string;
}

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Expand `#abc` to `#aabbcc`; leave anything else untouched. */
function expandShorthand(hex: string): string {
  const bare = hex.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$/.test(bare)) return hex;
  return `#${bare[0]}${bare[0]}${bare[1]}${bare[1]}${bare[2]}${bare[2]}`;
}

/**
 * ColorField Component
 *
 * A hex colour input paired with a native colour-picker swatch. The swatch and
 * the text field stay in sync: typing a valid hex moves the picker, and the
 * picker writes back a normalised `#rrggbb`.
 *
 * Laws:
 * - Matches Input's height, radius and border treatment
 * - The swatch is the affordance; the hex is for precision and pasting
 * - Invalid input is shown, not swallowed — the user's keystrokes are theirs
 */
export function ColorField({
  label,
  value,
  onChange,
  helperText,
  error,
  disabled,
  className,
}: ColorFieldProps) {
  const inputId = React.useId();
  const normalised = expandShorthand((value || '').trim());
  const isValid = HEX_PATTERN.test(normalised);
  const hasError = Boolean(error);

  return (
    <div className={clsx(styles.root, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}

      <div
        className={clsx(
          styles.control,
          hasError && styles.invalid,
          disabled && styles.disabled
        )}
      >
        <span className={styles.swatchWrapper}>
          {isValid ? (
            <input
              type="color"
              className={styles.swatch}
              value={normalised.toLowerCase()}
              disabled={disabled}
              aria-label={label ? `${label} colour picker` : 'Colour picker'}
              onChange={(event) => onChange(event.target.value)}
            />
          ) : (
            <span className={styles.swatchEmpty} aria-hidden="true" />
          )}
        </span>

        <input
          id={inputId}
          type="text"
          className={styles.hex}
          value={value ?? ''}
          disabled={disabled}
          placeholder="#000000"
          spellCheck={false}
          autoComplete="off"
          aria-invalid={hasError}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => {
            const expanded = expandShorthand(event.target.value.trim());
            if (expanded !== event.target.value) onChange(expanded);
          }}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {helperText && !error && <p className={styles.helper}>{helperText}</p>}
    </div>
  );
}

ColorField.displayName = 'ColorField';

export default ColorField;
